import { db, FirebaseUser } from '../../../firebase';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  setDoc,
  addDoc,
  arrayRemove,
  onSnapshot,
  writeBatch,
} from 'firebase/firestore';

import { TENANTS_COLLECTION_NAME } from './constants';
import { addActivity } from './activities';
import { trackEvent } from '../server/analytics/trackEvent';
import { ArchivedTeam, Team, TeamMap } from '../../types/team';
import { UserConfig } from '../../types/user';
import { userHasAccessToTeam } from '../../utils/accessUtils';
import { PlayerEntry, PlayerId } from '../../types/player';
import { getStrictPlayerId } from '../../utils/playerUtils';
import { Activity } from '../../types/activity';
import { PlatformPosition } from '../../types/position';
import { formationConfigsAreEqual, getPlayersWithNewFormation } from '../../utils/formationUtils';
import { deleteTeamFromAllAlerts } from './scouting';
import { FormationConfig, FormationConfigMap, PlatformFormation } from '../../types/formation';
import { staticTeamKeys } from '../../static/constants/teams';
import { defaultFormationConfigMap } from '../../static/constants/formations';


// get all teams
export const getTeams = (
  setTeams: (teams: TeamMap) => void,
  setFormationsInUse: (formationsInUse: PlatformFormation[]) => void,
  userConfig: UserConfig,
) => {

  const collectionRef = getTeamsCollectionRef(userConfig.club);

  const unsubscribe = onSnapshot(collectionRef, snapshot => {
    let teams: TeamMap = {};
    const formationsInUse = new Set<PlatformFormation>();

    snapshot.forEach((doc) => {

      const teamId = doc.id;
      const teamDoc = doc.data() as Team;

      if (userHasAccessToTeam(userConfig, teamId, teamDoc.isAcademyTeam ?? false, teamDoc.teamOwner ?? null)) {
        teams[teamId] = teamDoc;
      }

      // we would like to know which formations are in use, and the user may not have access to all teams, so we maintain this separate from the teams
      formationsInUse.add(teamDoc.formation);
    });

    teams = Object.fromEntries(
      Object.entries(teams).sort(([, a], [, b]) => {
        if ((a.isAcademyTeam ?? false) === (b.isAcademyTeam ?? false)) {
          return a.orderIndex - b.orderIndex;
        }
        return a.isAcademyTeam ? 1 : -1;
      })
    );

    setTeams(teams);
    setFormationsInUse(Array.from(formationsInUse));
  });

  return unsubscribe;
};


// add the given player to the given team, or change the position or position index of the player within the team
// the player is either
// (1) added to a new team
// (2) moved to a new position within the same team
// (3) moved to a new position index within the same position
export const addPlayerToTeam = async (
  playerEntry: PlayerEntry,
  newPositionKey: string,
  newPositionIndex: number | undefined,
  currentPositionKeyInTeam: string | undefined,
  teamId: string,
  team: Team,
  userEmail: string,
  tenant: string,
  actionSource: 'dragged' | 'modal',
  currentUser: FirebaseUser,
) => {

  const playerId = getStrictPlayerId(playerEntry.id);
  const teamDocRef = getTeamDocRef(teamId, tenant);

  // temporary tracking of potential issue
  if (playerEntry.id.toString().endsWith('copy')) {
    trackEvent(
      'UnexpectedBehavior',
      { file: 'teams.ts', behavior: 'Dragged player copy detected (addPlayerToTeam)' },
      currentUser,
      'user'
    );
  }

  try {
    let action: 'added' | 'positionChanged' | 'positionIndexChanged' = 'added';

    const currentPlayerList = team.players?.[newPositionKey] ?? [];
    const newPlayerList = currentPlayerList.filter(player => player.id !== playerId);

    // if newPositionIndex is not provided, the player is added to the end of the position array
    newPlayerList.splice(newPositionIndex ?? newPlayerList.length, 0, playerEntry);

    // (1) the player is added to a new team
    if (!currentPositionKeyInTeam) {

      // add player to new team
      await updateDoc(teamDocRef, {
        [`players.${newPositionKey}`]: newPlayerList
      });

      // log activity
      const activity: Activity = {
        userEmail: userEmail,
        date: new Date().toISOString(),
        teamId: teamId,
        teamName: team.name,
        teamIsAcademyTeam: team.isAcademyTeam ?? false,
        teamOwner: team.teamOwner ?? null,
        actionType: 'playerAdded',
        playerId: playerId,
        playerName: playerEntry.name,
      };
      addActivity(activity, tenant, currentUser);
    }

    else {
      // (2) the player is moved to another position within the same team
      if (currentPositionKeyInTeam !== newPositionKey) {
        action = 'positionChanged';
        await updateDoc(teamDocRef, {
          [`players.${newPositionKey}`]: newPlayerList,
          [`players.${currentPositionKeyInTeam}`]: arrayRemove(playerEntry),
        });
      }

      // (3) the player is moved to a new position index within the same position
      else {
        action = 'positionIndexChanged';
        await updateDoc(teamDocRef, {
          [`players.${newPositionKey}`]: newPlayerList,
        });
      }
    }

    trackEvent('PlayerMoved', { action, actionSource }, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'addPlayerToTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// remove the player with the given playerId from all teams
export const removePlayerFromAllTeams = async (
  playerId: PlayerId,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {
  try {
    const teamSnapshots = await getDocs(getTeamsCollectionRef(tenant));
    teamSnapshots.forEach((doc) => {
      removePlayerFromTeam(playerId, doc.id, doc.data() as Team, undefined, userEmail, tenant, currentUser);
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'removePlayerFromAllTeams', errorDetails: { error } }, currentUser, 'system');
  }
};


// remove the player with the given playerId from all academy teams
export const removePlayerFromAllAcademyTeams = async (
  playerId: PlayerId,
  teams: TeamMap,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {
  try {
    Object.entries(teams).forEach(([teamId, team]) => {
      if (team.isAcademyTeam) {
        removePlayerFromTeam(playerId, teamId, team, undefined, userEmail, tenant, currentUser);
      }
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'removePlayerFromAllAcademyTeams', errorDetails: { error } }, currentUser, 'system');
  }
};


// remove a player from a given team
export const removePlayerFromTeam = async (
  playerId: PlayerId,
  teamId: string,
  team: Team,
  currentPositionKey: string | undefined,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
  actionSource?: 'dragged' | 'modal', // controls whether the action is tracked
) => {

  playerId = getStrictPlayerId(playerId);

  const teamDocRef = getTeamDocRef(teamId, tenant);

  // temporary tracking of potential issue
  if (playerId.toString().endsWith('copy')) {
    trackEvent(
      'UnexpectedBehavior',
      { file: 'teams.ts', behavior: 'Dragged player copy detected (removePlayerFromTeam)' },
      currentUser,
      'user'
    );
  }

  try {

    let positionKey: string | undefined = currentPositionKey;
    if (!positionKey) {
      for (const position in team.players) {
        const playerList = team.players[position];
        if (playerList.some((player) => player.id === playerId)) {
          positionKey = position;
          break;
        }
      }
    }

    if (positionKey) {
      const playerList = team.players[positionKey] ?? [];
      const player = playerList.find((player: PlayerEntry) => player.id === playerId);

      if (player) {

        // remove player
        updateDoc(teamDocRef, {
          [`players.${positionKey}`]: arrayRemove(player)
        });

        // log activity
        const activity: Activity = {
          userEmail: userEmail,
          date: new Date().toISOString(),
          teamId: teamId,
          teamName: team.name,
          teamIsAcademyTeam: team.isAcademyTeam ?? false,
          teamOwner: team.teamOwner ?? null,
          actionType: 'playerRemoved',
          playerId: playerId,
          playerName: player.name,
        };
        addActivity(activity, tenant, currentUser);
      }
    }

    if (actionSource) {
      trackEvent('PlayerMoved', { action: 'removed', actionSource }, currentUser, 'user');
    }
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'removePlayerFromTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// update the team status of a player, where the player may be added and/or removed from multiple teams
export const updatePlayerTeamStatus = async (
  playerId: PlayerId,
  playerName: string,
  currentTeamPositionPairList: { teamId: string, position: PlatformPosition }[],
  newTeamPositionPairList: { teamId: string, position: PlatformPosition }[],
  teams: TeamMap,
  tenant: string,
  userEmail: string,
  currentUser: FirebaseUser,
) => {

  const strictPlayerId = getStrictPlayerId(playerId);

  const currentTeamIdToPosition = new Map();
  currentTeamPositionPairList.forEach(pair => {
    currentTeamIdToPosition.set(pair.teamId, pair.position);
  });
  const newTeamIdToPosition = new Map();
  newTeamPositionPairList.forEach(pair => {
    newTeamIdToPosition.set(pair.teamId, pair.position);
  });

  const promises = [];

  // remove players from teams where the player is no longer part of
  for (const [teamId, currentPosition] of currentTeamIdToPosition.entries()) {
    if (!newTeamIdToPosition.has(teamId)) {
      promises.push(
        removePlayerFromTeam(
          strictPlayerId,
          teamId,
          teams[teamId],
          currentPosition,
          userEmail,
          tenant,
          currentUser,
          'modal'
        )
      );
    }
  }

  // add players to new teams or new positions within the same team
  for (const [teamId, newPosition] of newTeamIdToPosition.entries()) {
    const currentPosition = currentTeamIdToPosition.get(teamId);
    if (!currentTeamIdToPosition.has(teamId) || currentPosition !== newPosition) {
      promises.push(
        addPlayerToTeam(
          { id: strictPlayerId, name: playerName },
          newPosition,
          undefined,
          currentTeamIdToPosition.has(teamId) ? currentPosition : undefined,
          teamId,
          teams[teamId],
          userEmail,
          tenant,
          'modal',
          currentUser
        )
      );
    }
  }

  try {
    await Promise.all(promises);
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updatePlayerTeamStatus', errorDetails: { error } }, currentUser, 'system');
  }
};


// create a new team
export const createTeam = async (
  name: string,
  formation: PlatformFormation,
  isAcademyTeam: boolean,
  teamOwner: string | null,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
): Promise<string | undefined> => {

  const teamsCollectionRef = getTeamsCollectionRef(tenant);

  // the orderIndex of the new team is the highest orderIndex (of teams within the same category) + 1
  let orderIndex = 0;
  const snapshot = await getDocs(teamsCollectionRef);
  snapshot.forEach(doc => {
    const teamDoc = doc.data() as Team;
    const teamIsWithinSameCategory = isAcademyTeam === (teamDoc.isAcademyTeam ?? false);
    if (teamIsWithinSameCategory && teamDoc.orderIndex >= orderIndex) {
      orderIndex = teamDoc.orderIndex + 1;
    }
  });

  const newTeam: Team = {
    players: {},
    name,
    formation,
    orderIndex,
  };

  if (isAcademyTeam) newTeam.isAcademyTeam = true;
  if (teamOwner) newTeam.teamOwner = teamOwner;

  try {
    const docRef = await addDoc(teamsCollectionRef, newTeam);

    const activity: Activity = {
      userEmail: userEmail,
      date: new Date().toISOString(),
      teamId: docRef.id,
      teamName: name,
      teamIsAcademyTeam: isAcademyTeam,
      teamOwner: teamOwner,
      actionType: 'teamCreated',
    };
    addActivity(activity, tenant, currentUser);

    trackEvent(isAcademyTeam ? 'AcademyTeamCreated' : 'ScoutTeamCreated', {}, currentUser, 'user');
    return docRef.id;
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'addTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// delete a team
// when a team is deleted, we also need to update all alerts that use this team explicitly
export const deleteTeam = async (
  teamId: string,
  team: Team,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const teamsCollectionRef = getTeamsCollectionRef(tenant);
  const batch = writeBatch(db);

  try {
    const snapshot = await getDocs(teamsCollectionRef);

    snapshot.forEach(doc => {
      if (doc.id === teamId) {
        batch.delete(doc.ref);
      }
      else {
        // must decrement the orderIndex of all teams (within the same category) with an orderIndex greater than the provided orderIndex
        const teamDoc = doc.data() as Team;
        const teamHasGreaterOrderIndex = teamDoc.orderIndex > team.orderIndex;
        const teamIsWithinSameCategory = (team.isAcademyTeam ?? false) === (teamDoc.isAcademyTeam ?? false);
        if (teamHasGreaterOrderIndex && teamIsWithinSameCategory) {
          batch.update(doc.ref, { orderIndex: teamDoc.orderIndex - 1 });
        }
      }
    });

    const activity: Activity = {
      userEmail: userEmail,
      date: new Date().toISOString(),
      teamId: teamId,
      teamName: team.name,
      teamIsAcademyTeam: team.isAcademyTeam ?? false,
      teamOwner: team.teamOwner ?? null,
      actionType: 'teamDeleted',
    };
    addActivity(activity, tenant, currentUser);
    addPlayerRemovedActivityForAllPlayersInTeam(teamId, team, userEmail, tenant, currentUser);

    await batch.commit();

    deleteTeamFromAllAlerts(teamId, tenant, currentUser);

    trackEvent(team.isAcademyTeam ? 'AcademyTeamDeleted' : 'ScoutTeamDeleted', {}, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'deleteTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// remove all players from the given team
export const resetTeam = async (
  teamId: string,
  team: Team,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const teamDocRef = getTeamDocRef(teamId, tenant);

  try {
    addPlayerRemovedActivityForAllPlayersInTeam(teamId, team, userEmail, tenant, currentUser);
    await updateDoc(teamDocRef, {
      players: {}
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'resetTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// log playerRemoved activities for all players in the given team
const addPlayerRemovedActivityForAllPlayersInTeam = async (
  teamId: string,
  team: Team,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  try {
    Object.values(team.players).forEach(playerList => {
      playerList.forEach((player: PlayerEntry) => {
        const activity: Activity = {
          userEmail: userEmail,
          date: new Date().toISOString(),
          teamId: teamId,
          teamName: team.name,
          teamIsAcademyTeam: team.isAcademyTeam ?? false,
          teamOwner: team.teamOwner ?? null,
          actionType: 'playerRemoved',
          playerId: player.id,
          playerName: player.name,
        };
        addActivity(activity, tenant, currentUser);
      });
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'addPlayerRemovedActivityForAllPlayersInTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// edit a team (update name and/or formation)
export const editTeam = async (
  teamId: string,
  team: Team,
  currentFormationConfig: FormationConfig,
  newName: string,
  newFormation: PlatformFormation,
  newFormationConfig: FormationConfig,
  newTeamOwner: string | null,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const teamDocRef = getTeamDocRef(teamId, tenant);

  let newPlayers = team.players;

  if (newFormation !== team.formation) {
    newPlayers = getPlayersWithNewFormation(team.players, newFormationConfig, currentFormationConfig);

    const activity: Activity = {
      userEmail: userEmail,
      date: new Date().toISOString(),
      teamId: teamId,
      teamName: team.name,
      teamIsAcademyTeam: team.isAcademyTeam ?? false,
      teamOwner: newTeamOwner ?? team.teamOwner ?? null,
      actionType: 'teamFormationEdited',
      newFormation: newFormation,
    };
    addActivity(activity, tenant, currentUser);
  }

  if (newName !== team.name) {
    const activity: Activity = {
      userEmail: userEmail,
      date: new Date().toISOString(),
      teamId: teamId,
      teamName: team.name,
      teamIsAcademyTeam: team.isAcademyTeam ?? false,
      teamOwner: newTeamOwner ?? team.teamOwner ?? null,
      actionType: 'teamNameEdited',
      oldName: team.name,
      newName: newName,
    };
    addActivity(activity, tenant, currentUser);
  }

  const newTeam: Team = {
    ...team,
    name: newName,
    formation: newFormation,
    teamOwner: newTeamOwner,
    players: newPlayers,
  };

  try {
    await setDoc(teamDocRef, newTeam);
    trackEvent(team.isAcademyTeam ? 'AcademyTeamAction' : 'ScoutTeamAction', { action: 'edited' }, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'editTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// re-order teams
export const reorderTeams = async (
  tabOptions: string[],
  isAcademyTeams: boolean,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const collectionRef = getTeamsCollectionRef(tenant);
  const batch = writeBatch(db);

  try {
    tabOptions.forEach((teamId, index) => {
      const docRef = doc(collectionRef, teamId);
      batch.update(docRef, { orderIndex: index });
    });

    await batch.commit();
    trackEvent(isAcademyTeams ? 'AcademyTeamAction' : 'ScoutTeamAction', { action: 'dragged' }, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'reorderTeams', errorDetails: { error } }, currentUser, 'system');
  }
};


// re-order restricted teams - restricted users must maintain the orderIndex slots of their teams
export const reorderRestrictedTeams = async (
  tabOptions: string[],
  teams: TeamMap,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const collectionRef = getTeamsCollectionRef(tenant);
  const batch = writeBatch(db);

  try {
    // create a sorted list of orderIndex values from the teams
    const orderIndexList = tabOptions.map(teamId => teams[teamId].orderIndex);
    orderIndexList.sort((a, b) => a - b);

    // update the orderIndex of each team in the tabOptions list
    tabOptions.forEach((teamId, index) => {
      const docRef = doc(collectionRef, teamId);
      batch.update(docRef, { orderIndex: orderIndexList[index] });
    });

    await batch.commit();
    trackEvent('ScoutTeamAction', { action: 'dragged' }, currentUser, 'user');
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'reorderTeams', errorDetails: { error } }, currentUser, 'system');
  }
};


// archive a team
export const archiveTeam = async (
  teamId: string,
  team: Team,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
): Promise<string | undefined> => {

  const archivedTeamsCollectionRef = collection(db, TENANTS_COLLECTION_NAME, tenant, 'archivedTeams');

  const archivedTeam: ArchivedTeam = {
    players: team.players,
    name: team.name,
    formation: team.formation,
    teamId: teamId,
    date: new Date().toISOString(),
  };

  if (team.isAcademyTeam) archivedTeam.isAcademyTeam = true;
  if (team.teamOwner) archivedTeam.teamOwner = team.teamOwner;

  try {
    const activity: Activity = {
      userEmail: userEmail,
      date: new Date().toISOString(),
      teamId: teamId,
      teamName: team.name,
      teamIsAcademyTeam: team.isAcademyTeam ?? false,
      teamOwner: team.teamOwner ?? null,
      actionType: 'teamArchived',
    };
    addActivity(activity, tenant, currentUser);

    const docRef = await addDoc(archivedTeamsCollectionRef, archivedTeam);

    trackEvent(team.isAcademyTeam ? 'AcademyTeamAction' : 'ScoutTeamAction', { action: 'archived' }, currentUser, 'user');

    return docRef.id;
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'archiveTeam', errorDetails: { error } }, currentUser, 'system');
  }
};


// update all teams that have the currentFormation (with the currentFormationConfig) to newFormation (with the newFormationConfig)
//     if isNewTenantFormation is true, we will also update all static teams (OwnTeam, MasterTeam, ShadowTeam)
//     when isNewTenantFormation is provided, static teams should always have currentFormation, but this is a safe guard to avvoid corrupt formation states
// currentFormation may be the same as newFormation, in which case we have updated the formation config in such a way that the teams need to be updated
// currentFormationConfig may be the same as newFormationConfig, in which case we have updated the name of a formation
export const updateTeamsWithNewFormation = async (
  newFormation: PlatformFormation,
  newFormationConfig: FormationConfig,
  currentFormation: PlatformFormation,
  currentFormationConfig: FormationConfig,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
  isNewTenantFormation?: boolean, // if true, we will update the static teams (OwnTeam, MasterTeam, ShadowTeam)
  formationConfigs?: FormationConfigMap,
) => {

  try {
    const teamSnapshots = await getDocs(getTeamsCollectionRef(tenant));

    teamSnapshots.forEach(async (doc) => {
      const teamId = doc.id;
      const teamDoc = doc.data() as Team;

      if (teamDoc.formation === currentFormation) {
        await updateTeamDocWithNewFormation(
          teamId,
          teamDoc,
          newFormation,
          newFormationConfig,
          currentFormationConfig,
          userEmail,
          tenant,
          currentUser,
        );
      }
      else if (isNewTenantFormation && formationConfigs && staticTeamKeys.includes(teamId)) {
        await updateTeamDocWithNewFormation(
          teamId,
          teamDoc,
          newFormation,
          newFormationConfig,
          formationConfigs[teamDoc.formation] ?? defaultFormationConfigMap[teamDoc.formation],
          userEmail,
          tenant,
          currentUser,
        );
      }
    });
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateTeamsWithNewFormation', errorDetails: { error } }, currentUser, 'system');
  }
};


// update a given team with new formation (or update team to be aligned with new formation config)
const updateTeamDocWithNewFormation = async (
  teamId: string,
  team: Team,
  newFormation: PlatformFormation,
  newFormationConfig: FormationConfig,
  currentFormationConfig: FormationConfig,
  userEmail: string,
  tenant: string,
  currentUser: FirebaseUser,
) => {

  const newTeam: Team = { ...team };

  const isNewFormation = newFormation !== team.formation;
  if (isNewFormation) {
    newTeam.formation = newFormation;
  }

  const isNewFormationConfig = !formationConfigsAreEqual(newFormationConfig, currentFormationConfig);
  if (isNewFormationConfig) {
    newTeam.players = getPlayersWithNewFormation(team.players, newFormationConfig, currentFormationConfig);
  }

  try {
    // we only log the activity if the formation of the team is updated, not if the formation config is updated
    if (isNewFormation) {
      const activity: Activity = {
        userEmail: userEmail,
        date: new Date().toISOString(),
        teamId: teamId,
        teamName: team.name,
        teamIsAcademyTeam: team.isAcademyTeam ?? false,
        teamOwner: team.teamOwner ?? null,
        actionType: 'teamFormationEdited',
        newFormation: newFormation,
      };
      addActivity(activity, tenant, currentUser);
    }

    await setDoc(doc(db, TENANTS_COLLECTION_NAME, tenant, 'teams', teamId), newTeam);
  }
  catch (error) {
    trackEvent('Error', { api: 'firestore', function: 'updateTeamDocWithNewFormation', errorDetails: { error } }, currentUser, 'system');
  }
};


const getTeamsCollectionRef = (tenant: string) => {
  return collection(db, TENANTS_COLLECTION_NAME, tenant, 'teams');
};


const getTeamDocRef = (teamId: string, tenant: string) => {
  return doc(db, TENANTS_COLLECTION_NAME, tenant, 'teams', teamId);
};
